import { useEffect, useState } from 'react';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { Box, Grid, Typography } from '@mui/material';

import { useThemeContext } from './contexts/BaseThemeContextProvider';
import HeaderBanner from './components/common/HeaderBanner';
import LightModeIcon from '@mui/icons-material/LightMode';
import PrimaryBlueButton from './components/common/buttons/PrimaryBlueButton';
import { fetchCognitoUserToken } from './utils/user';

import EditProfile from './EditProfile';
import Messages from './routes/Messages';
import PrivacyPolicy from './routes/PrivacyPolicy';
import Tos from './routes/tos';
import ProfileThemeToggle from './components/user/ProfileThemeToggle';

const backendURL = process.env.REACT_APP_BACKEND_URL;

export default function Profile() {
  const [userAttributes, setUserAttributes] = useState({});
  const [notificationCount, setNotificationCount] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedSection, setSelectedSection] = useState('Edit Profile');
  const { theme, handleLogOut } = useThemeContext();

  useEffect(() => {
    const getUserData = async () => {
      try {
        const attributes = await fetchUserAttributes();
        setUserAttributes(attributes);
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    const fetchNotificationCount = async () => {
      try {
        const res = await fetch(`${backendURL}/messages/get_unread_count`, {
          method: 'GET',
          headers: new Headers({
            Authorization: 'Bearer ' + (await fetchCognitoUserToken()),
          }),
        });
        if (res.ok) {
          const data = await res.json();
          setNotificationCount(data.count);
        } else {
          setErrorMessage('Failed to fetch notification count.');
        }
      } catch (error) {
        console.log('Failed to fetch notification count', error);
      }
    };

    fetchNotificationCount();
    getUserData();
  }, []);

  const onHandleLogOut = () => {
    handleLogOut();
  };

  // Handle section rendering
  const renderSelectedSection = () => {
    switch (selectedSection) {
      case 'Edit Profile':
        return <EditProfile />;
      case 'Messages':
        return <Messages />;
      case 'Privacy Policy':
        return <PrivacyPolicy />;
      case 'Terms of Service':
        return <Tos />;
      case 'Logout': {
        return onHandleLogOut();
      }
      case 'Appearance':
        return <ProfileThemeToggle />;
      default:
        return <EditProfile />;
    }
  };

  return (
    <Box
      style={{
        backgroundColor: theme.profile.backgroundColor,
        borderRadius: '16px',
        padding: '40px',
      }}
    >
      <HeaderBanner
        imageSrc={'/images/profile_header.png'}
        title={selectedSection}
        subtitle={`${userAttributes.name} ${userAttributes.family_name}`}
        description={`${userAttributes.email}`}
      />
      <Grid container spacing={2}>
        {/* Left Section with Buttons */}
        <Grid item xs={12} sm={4} md={5}>
          <PrimaryBlueButton
            sxProps={{
              padding: '16px 24px 16px 16px',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              border: 'none',
              color: theme.profile.leftMenu.button.color,
              backgroundColor:
                selectedSection === 'Edit Profile'
                  ? theme.profile.leftMenu.button.active.backgroundColor
                  : theme.profile.leftMenu.button.inactive.backgroundColor,
              '&:hover': {
                backgroundColor:
                  selectedSection === 'Edit Profile'
                    ? theme.profile.leftMenu.button.hover.backgroundColor
                    : theme.profile.leftMenu.button.hover.backgroundColor,
              },
              '&.MuiButton-root': {
                boxShadow: 'none',
              },
            }}
            additionalProps={{
              style: {
                borderRadius:
                  selectedSection === 'Edit Profile'
                    ? theme.profile.leftMenu.button.active.borderRadius
                    : theme.profile.leftMenu.button.inactive.borderRadius,
              },
              startIcon: (
                <img
                  src={
                    selectedSection === 'Edit Profile'
                      ? theme.profile.leftMenu.profile.activeIcon
                      : theme.profile.leftMenu.profile.inactiveIcon
                  }
                  alt={'Edit profile'}
                  style={{ width: '24px', height: '24px' }}
                />
              ),
            }}
            onClick={() => setSelectedSection('Edit Profile')}
            buttonText={'Edit Profile'}
          />
          <PrimaryBlueButton
            sxProps={{
              padding: '16px 24px 16px 16px',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              border: 'none',
              color: theme.profile.leftMenu.button.color,
              backgroundColor:
                selectedSection === 'Messages'
                  ? theme.profile.leftMenu.button.active.backgroundColor
                  : theme.profile.leftMenu.button.inactive.backgroundColor,
              '&:hover': {
                backgroundColor:
                  selectedSection === 'Messages'
                    ? theme.profile.leftMenu.button.hover.backgroundColor
                    : theme.profile.leftMenu.button.hover.backgroundColor,
              },
              '&.MuiButton-root': {
                boxShadow: 'none',
              },
            }}
            additionalProps={{
              style: {
                borderRadius:
                  selectedSection === 'Messages'
                    ? theme.profile.leftMenu.button.active.borderRadius
                    : theme.profile.leftMenu.button.inactive.borderRadius,
              },
              startIcon: (
                <img
                  src={
                    selectedSection === 'Messages'
                      ? theme.profile.leftMenu.messages.activeIcon
                      : theme.profile.leftMenu.messages.inactiveIcon
                  }
                  alt={'Messages'}
                  style={{ width: '24px', height: '24px' }}
                />
              ),
            }}
            onClick={() => setSelectedSection('Messages')}
            buttonText={
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Typography variant="body1" sx={{ color: theme.profile.leftMenu.button.color }}>
                  Messages
                </Typography>
                {notificationCount > 0 && (
                  <Box
                    sx={{
                      backgroundColor: theme.profile.leftMenu.messages.notificationCount.backgroundColor,
                      color: theme.profile.leftMenu.messages.notificationCount.color,
                      fontSize: '11px',
                      fontWeight: '500',
                      borderRadius: '100px',
                      border: theme.profile.leftMenu.messages.notificationCount.border,
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      marginLeft: 'auto',
                    }}
                  >
                    {notificationCount}
                  </Box>
                )}
              </Box>
            }
          />
          <PrimaryBlueButton
            sxProps={{
              padding: '16px 24px 16px 16px',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              border: 'none',
              color: theme.profile.leftMenu.button.color,
              backgroundColor:
                selectedSection === 'Appearance'
                  ? theme.profile.leftMenu.button.active.backgroundColor
                  : theme.profile.leftMenu.button.inactive.backgroundColor,
              '&:hover': {
                backgroundColor:
                  selectedSection === 'Appearance'
                    ? theme.profile.leftMenu.button.hover.backgroundColor
                    : theme.profile.leftMenu.button.hover.backgroundColor,
              },
              '&.MuiButton-root': {
                boxShadow: 'none',
              },
            }}
            additionalProps={{
              style: {
                borderRadius:
                  selectedSection === 'Appearance'
                    ? theme.profile.leftMenu.button.active.borderRadius
                    : theme.profile.leftMenu.button.inactive.borderRadius,
              },
              startIcon: (
                <LightModeIcon
                  sx={{
                    color:
                      selectedSection === 'Appearance'
                        ? theme.profile.leftMenu.appearance.activeColor
                        : theme.profile.leftMenu.appearance.inactiveColor,
                  }}
                />
              ),
            }}
            onClick={() => setSelectedSection('Appearance')}
            buttonText={'Appearance'}
          />
          <PrimaryBlueButton
            sxProps={{
              padding: '16px 24px 16px 16px',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              border: 'none',
              color: theme.profile.leftMenu.button.color,
              backgroundColor:
                selectedSection === 'Privacy Policy'
                  ? theme.profile.leftMenu.button.active.backgroundColor
                  : theme.profile.leftMenu.button.inactive.backgroundColor,
              '&:hover': {
                backgroundColor:
                  selectedSection === 'Privacy Policy'
                    ? theme.profile.leftMenu.button.hover.backgroundColor
                    : theme.profile.leftMenu.button.hover.backgroundColor,
              },
              '&.MuiButton-root': {
                boxShadow: 'none',
              },
            }}
            additionalProps={{
              style: {
                borderRadius:
                  selectedSection === 'Privacy Policy'
                    ? theme.profile.leftMenu.button.active.borderRadius
                    : theme.profile.leftMenu.button.inactive.borderRadius,
              },
              startIcon: (
                <img
                  src={
                    selectedSection === 'Privacy Policy'
                      ? theme.profile.leftMenu.privacyPolicy.activeIcon
                      : theme.profile.leftMenu.privacyPolicy.inactiveIcon
                  }
                  alt={'Privacy policy'}
                  style={{ width: '24px', height: '24px' }}
                />
              ),
            }}
            onClick={() => setSelectedSection('Privacy Policy')}
            buttonText={'Privacy Policy'}
          />
          <PrimaryBlueButton
            sxProps={{
              padding: '16px 24px 16px 16px',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              border: 'none',
              color: theme.profile.leftMenu.button.color,
              backgroundColor:
                selectedSection === 'Terms of Service'
                  ? theme.profile.leftMenu.button.active.backgroundColor
                  : theme.profile.leftMenu.button.inactive.backgroundColor,
              '&:hover': {
                backgroundColor:
                  selectedSection === 'Terms of Service'
                    ? theme.profile.leftMenu.button.hover.backgroundColor
                    : theme.profile.leftMenu.button.hover.backgroundColor,
              },
              '&.MuiButton-root': {
                boxShadow: 'none',
              },
            }}
            additionalProps={{
              style: {
                borderRadius:
                  selectedSection === 'Terms of Service'
                    ? theme.profile.leftMenu.button.active.borderRadius
                    : theme.profile.leftMenu.button.inactive.borderRadius,
              },
              startIcon: (
                <img
                  src={
                    selectedSection === 'Terms of Service'
                      ? theme.profile.leftMenu.termsOfService.activeIcon
                      : theme.profile.leftMenu.termsOfService.inactiveIcon
                  }
                  alt={'Terms of service'}
                  style={{ width: '24px', height: '24px' }}
                />
              ),
            }}
            onClick={() => setSelectedSection('Terms of Service')}
            buttonText={'Terms of Service'}
          />
          <PrimaryBlueButton
            sxProps={{
              padding: '16px 24px 16px 16px',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: '12px',
              width: '100%',
              border: 'none',
              color: theme.profile.leftMenu.button.color,
              backgroundColor:
                selectedSection === 'Logout'
                  ? theme.profile.leftMenu.button.active.backgroundColor
                  : theme.profile.leftMenu.button.inactive.backgroundColor,
              '&:hover': {
                backgroundColor:
                  selectedSection === 'Edit Profile'
                    ? theme.profile.leftMenu.button.hover.backgroundColor
                    : theme.profile.leftMenu.button.hover.backgroundColor,
              },
              '&.MuiButton-root': {
                boxShadow: 'none',
              },
            }}
            additionalProps={{
              style: {
                borderRadius:
                  selectedSection === 'Logout'
                    ? theme.profile.leftMenu.button.active.borderRadius
                    : theme.profile.leftMenu.button.inactive.borderRadius,
              },
              startIcon: (
                <img
                  src={
                    selectedSection === 'Logout'
                      ? theme.profile.leftMenu.logout.activeIcon
                      : theme.profile.leftMenu.logout.inactiveIcon
                  }
                  alt={'Logout'}
                  style={{ width: '24px', height: '24px' }}
                />
              ),
            }}
            onClick={() => {
              onHandleLogOut();
            }}
            buttonText={'Logout'}
          />
          {errorMessage && <div className="error-message">{errorMessage}</div>}
        </Grid>

        {/* Right Section with Selected Content */}
        <Grid item xs={12} sm={8} md={7}>
          <Box sx={{ width: '100%' }}>{renderSelectedSection()}</Box>
        </Grid>
      </Grid>
    </Box>
  );
}
